import { useEffect, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import {
  TailgateGeometrySidecarI,
  isTailgateGeometrySidecar,
} from '../types/tailgateGeometrySidecar'

type SidecarStatus = 'idle' | 'loading' | 'ready' | 'error'

const SENTRY_FEATURE = 'tailgate_grid_testing'

interface UseTailgateGridTestSidecarIProps {
  isEnabled: boolean
  jobId?: string
  sidecarAssetUrl?: string | null
}

export const useTailgateGridTestSidecar = ({
  isEnabled,
  jobId,
  sidecarAssetUrl,
}: UseTailgateGridTestSidecarIProps) => {
  const [sidecar, setSidecar] = useState<TailgateGeometrySidecarI | null>(null)
  const [status, setStatus] = useState<SidecarStatus>('idle')

  useEffect(() => {
    const shouldHaveSidecar = isEnabled && Boolean(jobId)
    setSidecar(null)
    setStatus(
      shouldHaveSidecar ? (sidecarAssetUrl ? 'loading' : 'error') : 'idle'
    )
    if (!shouldHaveSidecar || !sidecarAssetUrl) return

    const controller = new AbortController()

    const loadSidecar = async () => {
      try {
        const response = await fetch(sidecarAssetUrl, {
          signal: controller.signal,
        })
        if (!response.ok) {
          throw new Error(`Sidecar request failed with HTTP ${response.status}`)
        }

        const candidate: unknown = await response.json()
        if (!isTailgateGeometrySidecar(candidate)) {
          throw new Error('Sidecar response did not match schema v1')
        }

        setSidecar(candidate)
        setStatus('ready')
      } catch (cause) {
        if (controller.signal.aborted) return
        const failure = new Error('Unable to load tailgate geometry sidecar')
        Sentry.captureException(failure, {
          tags: {
            action: 'fetch_sidecar',
            feature: SENTRY_FEATURE,
          },
          extra: {
            jobId,
            failureType: cause instanceof Error ? cause.name : typeof cause,
          },
        })
        setStatus('error')
      }
    }

    void loadSidecar()
    return () => controller.abort()
  }, [isEnabled, jobId, sidecarAssetUrl])

  return {
    isError: status === 'error',
    isLoading: status === 'loading',
    sidecar,
  }
}
